import {
  type TCreateWorkSchedulePayload,
  type TGetWorkScheduleListParams,
  type TUpdateWorkSchedulePayload,
  type TWorkScheduleAttachmentResponse,
  type TWorkScheduleCompany,
  type TWorkScheduleDetail,
  type TWorkScheduleDetailResponse,
  type TWorkScheduleEvent,
  type TWorkScheduleVisibleCompany,
  type TWorkScheduleVisibleCompanyResponse,
} from 'features/work-schedule/types';
import { baseApi } from 'lib/rtkQuery/baseApi';
import { QueryTags } from 'lib/rtkQuery/constants';
import { invalidatesTags } from 'lib/rtkQuery/helpers';

const endpoint = `/work-schedules`;

const normalizeWorkScheduleAttachment = ({
  file_title,
  file_name,
  ...attachment
}: TWorkScheduleAttachmentResponse) => ({
  ...attachment,
  file_title,
  file_name: file_name || file_title || '',
});

const normalizeWorkScheduleDetail = ({
  attachment,
  attachments,
  ...detail
}: TWorkScheduleDetailResponse): TWorkScheduleDetail => ({
  ...detail,
  attachments: (attachments ?? attachment ?? []).map(
    normalizeWorkScheduleAttachment
  ),
});

const normalizeVisibleCompany = ({
  company_code,
  code,
  ...company
}: TWorkScheduleVisibleCompanyResponse): TWorkScheduleVisibleCompany => ({
  ...company,
  code,
  company_code: company_code ?? code ?? '',
});

export const workScheduleApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      QueryTags.WorkScheduleList,
      QueryTags.WorkScheduleDetail,
      QueryTags.WorkScheduleCompany,
    ],
  })
  .injectEndpoints({
    endpoints: builder => ({
      GetWorkScheduleCompanies: builder.query<TWorkScheduleCompany[], void>({
        query: () => ({ url: `${endpoint}/companies` }),
        providesTags: [QueryTags.WorkScheduleCompany],
      }),

      GetWorkScheduleList: builder.query<
        TWorkScheduleEvent[],
        TGetWorkScheduleListParams
      >({
        query: params => ({ url: endpoint, params }),
        providesTags: [QueryTags.WorkScheduleList],
      }),

      GetWorkScheduleDetail: builder.query<TWorkScheduleDetail, string>({
        query: scheduleCode => ({ url: `${endpoint}/${scheduleCode}` }),
        transformResponse: (response: TWorkScheduleDetailResponse) =>
          normalizeWorkScheduleDetail(response),
        providesTags: [QueryTags.WorkScheduleDetail],
      }),

      CreateWorkSchedule: builder.mutation<
        TWorkScheduleDetail,
        TCreateWorkSchedulePayload
      >({
        query: data => ({ url: endpoint, method: 'POST', data }),
        invalidatesTags: invalidatesTags([QueryTags.WorkScheduleList]),
      }),

      UpdateWorkSchedule: builder.mutation<
        TWorkScheduleDetail,
        { scheduleCode: string; payload: TUpdateWorkSchedulePayload }
      >({
        query: ({ scheduleCode, payload }) => ({
          url: `${endpoint}/${scheduleCode}`,
          method: 'PUT',
          data: payload,
        }),
        invalidatesTags: invalidatesTags([
          QueryTags.WorkScheduleList,
          QueryTags.WorkScheduleDetail,
        ]),
      }),

      DeleteWorkSchedule: builder.mutation<void, string>({
        query: scheduleCode => ({
          url: `${endpoint}/${scheduleCode}`,
          method: 'DELETE',
        }),
        invalidatesTags: invalidatesTags([QueryTags.WorkScheduleList]),
      }),

      GetWorkScheduleVisibleCompanies: builder.query<
        TWorkScheduleVisibleCompany[],
        void
      >({
        query: () => ({ url: `${endpoint}/visible-companies` }),
        transformResponse: (response: TWorkScheduleVisibleCompanyResponse[]) =>
          response
            .map(normalizeVisibleCompany)
            .filter(item => item.company_code),
        providesTags: [QueryTags.WorkScheduleCompany],
      }),

      UpdateWorkScheduleVisibleCompanies: builder.mutation<void, string[]>({
        query: companyCodes => ({
          url: `${endpoint}/visible-companies`,
          method: 'PUT',
          data: { company_codes: companyCodes },
        }),
        invalidatesTags: invalidatesTags([QueryTags.WorkScheduleCompany]),
      }),
    }),
  });

export const {
  useGetWorkScheduleCompaniesQuery,
  useGetWorkScheduleListQuery,
  useGetWorkScheduleDetailQuery,
  useCreateWorkScheduleMutation,
  useUpdateWorkScheduleMutation,
  useDeleteWorkScheduleMutation,
  useGetWorkScheduleVisibleCompaniesQuery,
  useUpdateWorkScheduleVisibleCompaniesMutation,
} = workScheduleApi;
